import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { metricService, Metric, MetricValue } from '../../services/metricService';
import PageHeader from '../common/PageHeader';
import './MetricDetail.css';

interface MetricDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

interface TableData {
  columns: string[];
  rows: any[][];
}

const MetricDetail: React.FC<MetricDetailProps> = ({ user, onToggleSidebar }) => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const [metric, setMetric] = useState<Metric | null>(null);
  const [metricValue, setMetricValue] = useState<MetricValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loadingTable, setLoadingTable] = useState(false);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [availableDimensions, setAvailableDimensions] = useState<Array<{ id: string; label: string }>>([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    category: 'habits',
    unit: 'count',
    is_active: true
  });

  useEffect(() => {
    if (name && user?.id) {
      loadMetricData();
      loadDimensionOptions();
    }
  }, [name, user]);

  useEffect(() => {
    if (showTable && selectedDimensions.length > 0 && metric && user?.id) {
      loadTableData();
    }
  }, [showTable, selectedDimensions, metric, user]);

  const loadMetricData = async () => {
    if (!name || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const metricResponse = await metricService.getMetricByName(name);
      
      if (metricResponse.success && metricResponse.metric) {
        const fetchedMetric = metricResponse.metric;
        // Handle configuration if it comes as a string
        if (typeof fetchedMetric.configuration === 'string') {
          try {
            fetchedMetric.configuration = JSON.parse(fetchedMetric.configuration as unknown as string);
          } catch (e) {
            console.error("Failed to parse metric configuration", e);
          }
        }

        setMetric(fetchedMetric);
        
        // Initialize selected dimensions from configuration if available
        if (fetchedMetric.configuration?.dimensions && fetchedMetric.configuration.dimensions.length > 0) {
           setSelectedDimensions(fetchedMetric.configuration.dimensions);
        }

        setLoading(false);

        setCalculating(true);
        const valueResponse = await metricService.calculateMetric(name, user.id);
        if (valueResponse.success && valueResponse.metric) {
          setMetricValue(valueResponse.metric);
        }
        setCalculating(false);
      } else {
        setError(metricResponse.message || 'Metric not found');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading metric:', err);
      setError('Failed to load metric. Please try again.');
      setLoading(false);
      setCalculating(false);
    }
  };

  const loadDimensionOptions = async () => {
    try {
      const response = await metricService.getMetricBuilderOptions();
      if (response.success && response.dimensions) {
        setAvailableDimensions(response.dimensions);
      }
    } catch (err) {
      console.error('Error loading dimension options:', err);
    }
  };

  const loadTableData = async () => {
    if (!metric || !user?.id || selectedDimensions.length === 0) return;

    try {
      setLoadingTable(true);
      setError(null);

      let metricType = 'habit_completion';
      let filters = {};
      
      // Use saved configuration if available
      if (metric.configuration) {
        metricType = metric.configuration.metric;
        filters = metric.configuration.filters || {};
      } else if (metric.category === 'habits') {
        // Fallback logic for legacy metrics
        // Check if metric name suggests measurement (contains words like 'measurement', 'value', 'amount')
        const nameLower = metric.name.toLowerCase();
        if (nameLower.includes('measurement') || nameLower.includes('value') || nameLower.includes('amount')) {
          metricType = 'habit_measurement';
        } else {
          metricType = 'habit_completion';
        }
      }
      
      const response = await metricService.buildMetric({
        metric: metricType,
        dimensions: selectedDimensions,
        filters: filters
      });

      if (response.success && response.columns && response.rows) {
        setTableData({
          columns: response.columns,
          rows: response.rows
        });
      } else {
        // Try the other metric type if first attempt failed (only for legacy metrics without config)
        if (!metric.configuration && metric.category === 'habits' && metricType === 'habit_completion') {
          const altResponse = await metricService.buildMetric({
            metric: 'habit_measurement',
            dimensions: selectedDimensions,
            filters: {}
          });
          if (altResponse.success && altResponse.columns && altResponse.rows) {
            setTableData({
              columns: altResponse.columns,
              rows: altResponse.rows
            });
          } else {
            setError(altResponse.message || 'Failed to load table data');
          }
        } else {
          setError(response.message || 'Failed to load table data');
        }
      }
    } catch (err) {
      console.error('Error loading table data:', err);
      setError('Failed to load table data. Please try again.');
    } finally {
      setLoadingTable(false);
    }
  };

  const handleDimensionToggle = (dimensionId: string) => {
    setSelectedDimensions(prev => {
      if (prev.includes(dimensionId)) {
        return prev.filter(d => d !== dimensionId);
      } else {
        return [...prev, dimensionId];
      }
    });
  };

  const handleShowTable = () => {
    if (!showTable && selectedDimensions.length === 0) {
      // Default to 'week' dimension for habit metrics
      setSelectedDimensions(['week']);
    }
    setShowTable(!showTable);
  };

  const handleRecalculate = async () => {
    if (!name || !user?.id) return;

    try {
      setCalculating(true);
      setError(null);
      const valueResponse = await metricService.calculateMetric(name, user.id);
      if (valueResponse.success && valueResponse.metric) {
        setMetricValue(valueResponse.metric);
      } else {
        setError(valueResponse.message || 'Failed to calculate metric');
      }
    } catch (err) {
      console.error('Error calculating metric:', err);
      setError('Failed to calculate metric. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  const handleEdit = () => {
    if (!metric) return;
    setEditFormData({
      name: metric.name,
      description: metric.description || '',
      category: metric.category,
      unit: metric.unit,
      is_active: metric.is_active
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!name || !metric) return;

    try {
      setEditing(true);
      setError(null);

      const updates: any = {
        name: editFormData.name.trim(),
        description: editFormData.description.trim() || undefined,
        category: editFormData.category,
        unit: editFormData.unit,
        is_active: editFormData.is_active
      };

      // Preserve configuration if it exists
      if (metric.configuration) {
        updates.configuration = metric.configuration;
      }

      const response = await metricService.updateMetric(name, updates);
      
      if (response.success && response.metric) {
        // Handle configuration parsing if needed
        const updatedMetric = response.metric;
        if (typeof updatedMetric.configuration === 'string') {
          try {
            updatedMetric.configuration = JSON.parse(updatedMetric.configuration as unknown as string);
          } catch (e) {
            console.error("Failed to parse metric configuration", e);
          }
        }
        
        setMetric(updatedMetric);
        setShowEditModal(false);
        
        // If name changed, navigate to new URL
        if (updates.name !== name) {
          navigate(`/metrics/${encodeURIComponent(updates.name)}`);
        }
      } else {
        setError(response.message || 'Failed to update metric');
      }
    } catch (err) {
      console.error('Error updating metric:', err);
      setError('Failed to update metric. Please try again.');
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!name) return;
    if (!window.confirm(`Are you sure you want to delete the metric "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await metricService.deleteMetric(name);
      if (response.success) {
        navigate('/metrics');
      } else {
        setError(response.message || 'Failed to delete metric');
      }
    } catch (err) {
      console.error('Error deleting metric:', err);
      setError('Failed to delete metric. Please try again.');
    }
  };

  const rightActions = (
    <div className="metric-options-menu">
      <button
        className="options-menu-btn"
        onClick={() => setShowOptionsMenu(!showOptionsMenu)}
        title="More options"
      >
        ⋮
      </button>
      {showOptionsMenu && (
        <div className="options-menu-dropdown">
          <button
            className="options-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit();
              setShowOptionsMenu(false);
            }}
          >
            ✏️ Edit Metric
          </button>
          <button
            className="options-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              handleRecalculate();
              setShowOptionsMenu(false);
            }}
            disabled={calculating}
          >
            {calculating ? '⏳ Recalculating...' : '↻ Recalculate'}
          </button>
          <button
            className="options-menu-item delete-option"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
              setShowOptionsMenu(false);
            }}
          >
            🗑️ Delete Metric
          </button>
        </div>
      )}
      {showOptionsMenu && (
        <div
          className="options-menu-overlay"
          onClick={() => setShowOptionsMenu(false)}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="metric-detail-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1>Metric Detail</h1>}
          leftActions={
            <button
              onClick={() => navigate('/metrics')}
              className="metric-detail-back-button"
              title="Back to Metrics"
            >
              ← Back
            </button>
          }
        />
        <div className="metric-detail-loading">
          <p>Loading metric...</p>
        </div>
      </div>
    );
  }

  if (error && !metric) {
    return (
      <div className="metric-detail-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1>Metric Detail</h1>}
          leftActions={
            <button
              onClick={() => navigate('/metrics')}
              className="metric-detail-back-button"
              title="Back to Metrics"
            >
              ← Back
            </button>
          }
        />
        <div className="metric-detail-error">
          <p>{error}</p>
          <button onClick={() => navigate('/metrics')} className="metric-detail-back-btn">
            Back to Metrics
          </button>
        </div>
      </div>
    );
  }

  if (!metric) {
    return null;
  }

  return (
    <div className="metric-detail-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="metric-detail-title">{metric.name}</h1>}
        leftActions={
          <button
            onClick={() => navigate('/metrics')}
            className="metric-detail-back-button"
            title="Back to Metrics"
          >
            ← Back
          </button>
        }
        rightActions={rightActions}
      />

      {error && (
        <div className="metric-detail-error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-close-btn">×</button>
        </div>
      )}

      <div className="metric-detail-content">
        <div className="metric-detail-section">
          <div className="metric-detail-info">
            <div className="metric-detail-info-item">
              <span className="metric-detail-label">Category</span>
              <span className="metric-detail-value">{metric.category}</span>
            </div>
            <div className="metric-detail-info-item">
              <span className="metric-detail-label">Unit</span>
              <span className="metric-detail-value">{metric.unit}</span>
            </div>
            <div className="metric-detail-info-item">
              <span className="metric-detail-label">Status</span>
              <span className={`metric-detail-status ${metric.is_active ? 'active' : 'inactive'}`}>
                {metric.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {metric.description && (
          <div className="metric-detail-section">
            <h2 className="metric-detail-section-title">Description</h2>
            <p className="metric-detail-description">{metric.description}</p>
          </div>
        )}

        <div className="metric-detail-section">
          <h2 className="metric-detail-section-title">Current Value</h2>
          <div className="metric-detail-value-display">
            {calculating ? (
              <div className="metric-detail-value-loading">
                <span className="loading-spinner"></span>
                <span>Calculating...</span>
              </div>
            ) : metricValue ? (
              <>
                <div className="metric-detail-value-large">
                  {metricService.formatMetricValue(metricValue.value, metricValue.unit)}
                </div>
                <div className="metric-detail-unit-label">{metric.unit}</div>
              </>
            ) : (
              <div className="metric-detail-value-error">
                Unable to calculate metric value
              </div>
            )}
          </div>
        </div>

        {metric.category === 'habits' && availableDimensions.length > 0 && (
          <div className="metric-detail-section">
            <div className="metric-detail-section-header">
              <h2 className="metric-detail-section-title">Data Table</h2>
              <button
                onClick={handleShowTable}
                className="metric-detail-toggle-btn"
              >
                {showTable ? 'Hide' : 'Show'} Table
              </button>
            </div>
            
            {showTable && (
              <>
                <div className="metric-detail-dimensions-selector">
                  <label className="metric-detail-dimensions-label">Group by:</label>
                  <div className="metric-detail-dimensions-list">
                    {availableDimensions.map((dim) => (
                      <label key={dim.id} className="metric-detail-dimension-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedDimensions.includes(dim.id)}
                          onChange={() => handleDimensionToggle(dim.id)}
                        />
                        <span>{dim.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {loadingTable ? (
                  <div className="metric-detail-table-loading">
                    <span className="loading-spinner"></span>
                    <span>Loading table data...</span>
                  </div>
                ) : tableData && tableData.rows.length > 0 ? (
                  <div className="metric-detail-table-container">
                    <table className="metric-detail-table">
                      <thead>
                        <tr>
                          {tableData.columns.map((col, idx) => (
                            <th key={idx}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.rows.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx}>
                                {cellIdx === row.length - 1 && typeof cell === 'number'
                                  ? metricService.formatMetricValue(cell, metric.unit)
                                  : String(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : tableData && tableData.rows.length === 0 ? (
                  <div className="metric-detail-table-empty">
                    No data available for the selected dimensions
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>

      {showEditModal && (
        <div className="metric-detail-edit-modal-overlay" onClick={() => !editing && setShowEditModal(false)}>
          <div className="metric-detail-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="metric-detail-edit-modal-header">
              <h2 className="metric-detail-edit-modal-title">Edit Metric</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="metric-detail-edit-modal-close"
                disabled={editing}
              >
                ×
              </button>
            </div>
            
            <div className="metric-detail-edit-form">
              <div className="metric-detail-edit-field">
                <label className="metric-detail-edit-label">Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="metric-detail-edit-input"
                  disabled={editing}
                />
              </div>
              
              <div className="metric-detail-edit-field">
                <label className="metric-detail-edit-label">Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="metric-detail-edit-textarea"
                  rows={3}
                  disabled={editing}
                />
              </div>
              
              <div className="metric-detail-edit-field-row">
                <div className="metric-detail-edit-field">
                  <label className="metric-detail-edit-label">Category</label>
                  <select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                    className="metric-detail-edit-select"
                    disabled={editing}
                  >
                    <option value="habits">Habits</option>
                    <option value="fitness">Fitness</option>
                    <option value="health">Health</option>
                    <option value="productivity">Productivity</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="metric-detail-edit-field">
                  <label className="metric-detail-edit-label">Unit</label>
                  <select
                    value={editFormData.unit}
                    onChange={(e) => setEditFormData({ ...editFormData, unit: e.target.value })}
                    className="metric-detail-edit-select"
                    disabled={editing}
                  >
                    <option value="count">Count</option>
                    <option value="percentage">Percentage</option>
                    <option value="currency">Currency</option>
                    <option value="days">Days</option>
                    <option value="minutes">Minutes</option>
                  </select>
                </div>
              </div>
              
              <div className="metric-detail-edit-field">
                <label className="metric-detail-edit-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    disabled={editing}
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>
            
            <div className="metric-detail-edit-actions">
              <button
                onClick={handleEditSave}
                disabled={!editFormData.name.trim() || editing}
                className="metric-detail-edit-btn metric-detail-edit-btn-primary"
              >
                {editing ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="metric-detail-edit-btn metric-detail-edit-btn-secondary"
                disabled={editing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricDetail;

